// Customer-app analytics dispatcher — mirrors landing-page/src/utils/analytics.js
// so GTM/GA4 receives the same event vocabulary across marketing site and
// signed-in portal.
//
// Guardrails:
//   1. NEVER include PII (name, email, phone, NRIC, weights, diagnoses…) —
//      keys in BLOCKED_PARAM_KEYS are stripped before dispatch.
//   2. Only events in ALLOWED_EVENTS are forwarded; unknown ones are logged
//      and dropped so a typo can't populate GA4 with garbage names.
//
// Falls back gracefully if GTM/GA4 isn't configured — pushes to a queue in
// window.dataLayer either way, and no-ops when window is unavailable (SSR).

const ALLOWED_EVENTS = new Set([
  'page_view',
  'cta_click',
  // GA4 conversion events per MyDrAI_SEO_Master.doc §6
  'assessment_started',
  'otp_verified',
  'begin_checkout',
  'purchase',
  'consultation_completed_patient',
  'waitlist_submitted',
]);

const BLOCKED_PARAM_KEYS = new Set([
  'bmi', 'weight', 'weight_kg', 'height', 'height_cm', 'age',
  'diagnosis', 'medication', 'medications', 'allergy', 'symptom', 'symptoms',
  'pregnancy', 'condition', 'red_flag', 'red_flags', 'contraindication',
  'name', 'email', 'phone', 'mobile', 'nric', 'ic',
]);

function sanitize(params) {
  if (!params || typeof params !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (BLOCKED_PARAM_KEYS.has(k.toLowerCase())) continue;
    if (v == null) continue;
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') out[k] = v;
  }
  return out;
}

export function track(eventName, params = {}) {
  if (!ALLOWED_EVENTS.has(eventName)) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(`[analytics] event "${eventName}" is not in the approved list — skipped.`);
    }
    return;
  }
  if (typeof window === 'undefined') return;

  const clean = sanitize(params);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...clean });
  if (typeof window.gtag === 'function') window.gtag('event', eventName, clean);
}

// ─── Convenience helpers (SEO Master §6) ────────────────────────────────────

export function trackOtpVerified({ purpose } = {}) {
  track('otp_verified', { purpose });
}

export function trackBeginCheckout({ value = 1599.00, currency = 'MYR', order_id, package_code } = {}) {
  track('begin_checkout', { value, currency, order_id, package_code });
}

export function trackPurchase({ transaction_id, value = 1599.00, currency = 'MYR', package_code } = {}) {
  track('purchase', { transaction_id, value, currency, package_code });
}

export function trackConsultationCompletedPatient({ booking_id } = {}) {
  track('consultation_completed_patient', { booking_id });
}
